import * as moment from 'moment';
import * as _ from 'underscore';
import { util } from '../util';
import { IBPProfileBasic, ICompany, ICompanyCountry, IContact, IContactExtended, IContactType, ICountry, ICurrency, IDepartment, IExtendedWindow, ILabelledValue, ILanguage, ILink, ILocation, ILocationExtended, ILov, IObject, IState, IUser, IValueObject } from '../../../type_definitions/datatypes';
import { CurrencyService } from './currency.service';
import { DIACRITICS_MAP } from './formatting-data.service';
import { IUserData } from './auth/auth-data.service';
import { ITicketCategory } from '../ticket/ticket-data.service';
import { DEFAULT_DATE_FORMAT } from './date.util';
import { Inject, Injectable } from '@angular/core';
import { TranslationService } from '../translation/translation.service';
import { RoleContactType } from '../contact/role-contact-type';

/**
 * @ngdoc service
 * @name SharedModule:formattingService
 *
 * @description
 * With the formattingService we can format a piece of data to human-readable output.
 *
 */
@Injectable()
export class FormattingService {
  constructor(
    @Inject(Window) private window:IExtendedWindow,
    private translationService:TranslationService,
    private currencyService:CurrencyService
  ) {
    /**/
  }

  /**
   * @ngdoc method
   * @name SharedModule:formattingService#formatDate
   * @methodOf SharedModule:formattingService
   *
   * @description Format a date (without timezone) to DD/MM/YYYY
   *
   * @param {string|Date} date String or Date representing a date
   * @param {string=} format String representing the format of the date
   *
   * @return {string} The formatted date
   */
  public formatDate(date:number | string | Date, format?:string):string {
    if (util.isNullOrUndefined(date) || date === '') {
      return '';
    }
    format = format || DEFAULT_DATE_FORMAT;
    const m = moment(date).utc();
    return m.format(format);
  }

  /**
   * @ngdoc method
   * @name SharedModule:formattingService#formatDateNoUtc
   * @methodOf SharedModule:formattingService
   *
   * @description Format a date excluding UTC to DD/MM/YYYY
   *
   * @param {string} dateString - date
   * @param {string=} format - format of the date
   *
   * @return {string} The formatted date excluding UTC
   */
  public formatDateNoUtc(dateString:string, format?:string):string {
    if (util.isNullOrUndefined(dateString) || dateString === '') {
      return '';
    }
    format = format || DEFAULT_DATE_FORMAT;
    const m = moment(dateString);
    return m.format(format);
  }

  /**
   * @ngdoc method
   * @name SharedModule:formattingService#parseDate
   * @methodOf SharedModule:formattingService
   *
   * @description Parse a date without timezone information
   *
   * @param {string} dateString - date
   * @param {string=} format - format of the date
   *
   * @return {Date} The parsed date without timezone
   */
  public parseDate(dateString:string, format?:string):Date {
    if (util.isNullOrUndefined(dateString) || dateString === '') {
      return null;
    }
    format = format || DEFAULT_DATE_FORMAT;
    const m = moment.utc(dateString, format);

    return m.toDate();
  }

  /**
   * @ngdoc method
   * @name SharedModule:formattingService#formatDateMedium
   * @methodOf SharedModule:formattingService
   *
   * @description Format a date to medium format: D MMM YYYY
   *
   * @param {string} dateString - date
   *
   * @return {string} The formatted date in medium format
   */
  public formatDateMedium = (dateString:string | number):string => {
    return this.formatDate(dateString, 'D MMM YYYY');
  };

  /**
   * @ngdoc method
   * @name SharedModule:formattingService#join
   * @methodOf SharedModule:formattingService
   *
   * @description Joins a list of arguments with a separator, while filtering out arguments that are null, undefined or empty.
   * `e.g. join('a', '', 'b', null, 'c', ',') => 'a,b,c'`
   *
   * @param {any} args A variable list of arguments (allows n parameters)
   *
   * @return {string} The string concatenating all arguments (n-1) using a separator (n)
   */
  public join(...args:any[]):string {
    if (!args.length) {
      return undefined;
    }
    // eslint-disable-next-line prefer-rest-params
    const separator:string = args[ args.length - 1 ];
    // eslint-disable-next-line prefer-rest-params
    const operands:any[] = _.flatten(Array.prototype.slice.call(args, 0, args.length - 1));

    return _.filter(operands, o => util.notNullOrUndefinedOrEmpty(o)).join(separator);
  }

  /**
   * @ngdoc method
   * @name SharedModule:formattingService#formatDateTimeMedium
   * @methodOf SharedModule:formattingService
   *
   * @description format a date to D MMM YYYY HH:mm format
   *
   * @param {string} dateString - string representing a date
   *
   * @return {string} -
   */
  public formatDateTimeMedium(dateString:string):string {
    if (util.isNullOrUndefined(dateString) || dateString === '') {
      return '';
    }
    return moment(dateString).utc().format('D MMM YYYY HH:mm');
  }

  /**
   * @ngdoc method
   * @name SharedModule:formattingService#formatCurrencyL
   * @methodOf SharedModule:formattingService
   *
   *
   * @description
   * Long format for both
   * `{ "id" : "USD", "value" : "US Dollar", "sequence" : 2 }`
   * and
   * `{ "isoCode" : "AED", "description" : "UAE Dirham" }`
   *
   * @param {ICurrency} currency a currency object
   *
   * @return {string} a formatted currency: ISOCODE (DESCRIPTION)
   */
  public formatCurrencyL(currency:ICurrency):string {
    if (!_.isObject(currency)) {
      return '';
    }

    if (currency.id) {
      return currency.id + (currency.value? ' (' + currency.value + ')' : '');
    } else {
      return currency.isoCode + (currency.description? ' (' + currency.description + ')' : '');
    }
  }

  /**
   * @ngdoc method
   * @name SharedModule:formattingService#formatCurrencyS
   * @methodOf SharedModule:formattingService
   *
   *
   * @description
   * Short format for both
   * `{ "id" : "USD", "value" : "US Dollar", "sequence" : 2 }`
   * and
   * `{ "isoCode" : "AED", "description" : "UAE Dirham" }`
   *
   * @param {ICurrency} currency a currency object
   *
   * @return {string} a formatted currency: ISOCODE
   */
  public formatCurrencyS(currency:ICurrency):string {
    if (!currency) {
      return '';
    }

    return currency.id? currency.id : currency.isoCode;
  }

  /**
   * @ngdoc method
   * @name SharedModule:formattingService#formatBoolean
   * @methodOf SharedModule:formattingService
   *
   * @description turns bool variable in 'Yes' or 'No' string
   *
   * @param {(string|boolean)} bool - bool
   *
   * @return {string} 'Yes' or 'No'
   */
  public formatBoolean(bool:string | boolean | IValueObject):string {
    if (util.isNullOrUndefined(bool)) {
      return '';
    }

    const isFalse = (value:string | boolean) => {
      if (_.isString(value)) {
        return (<string>value).toUpperCase() === 'FALSE' || (<string>value).toUpperCase() === 'NO';
      } else {
        return !value;
      }
    };

    //noinspection JSAccessibilityCheck
    if ((_.isObject(bool) && isFalse((<any>bool).id)) || isFalse(<string | boolean>bool)) {
      return 'No';
    }
    return 'Yes';
  }

  /**
   * @ngdoc method
   * @name SharedModule:formattingService#formatFullAddress
   * @methodOf SharedModule:formattingService
   *
   * @description Format an address based on the defined method parameters (multi lines)
   *
   * @param {string} address - address
   * @param {string} zip - zip
   * @param {string} city - city
   * @param {IState} state - state
   * @param {ICountry} country - country
   *
   * @return {string} The complete address formatted in multiple lines
   */
  public formatFullAddress(address:string, zip:string, city:string, state:IState, country:ICountry|string):string {
    let fullAddress = '';
    if (address) {
      fullAddress += address + '\n';
    }
    if (zip) {
      fullAddress += zip + ' ';
    }
    if (city) {
      fullAddress += city + '\n';
    }
    if (state) {
      fullAddress += state.name + '\n';
    }
    if (country) {
      fullAddress += (<ICountry>country)?.name? (<ICountry>country).name : country;
    }
    return fullAddress;
  }

  /**
   * @ngdoc method
   * @name SharedModule:formattingService#formatAddressLine
   * @methodOf SharedModule:formattingService
   *
   * @description Format an address based on the defined method parameters (one line)
   *
   * @param {string} address - address
   * @param {string} zip - zip
   * @param {string} city - city
   * @param {string=} state - state
   * @param {string=} country - country
   *
   * @return {string} The complete address formatted in one line
   */
  public formatAddressLine(address:string, zip:string, city:string, state?:string, country?:string):string {

    let addressLine = '';
    if (address) {
      addressLine += address + ', ';
    }
    if (zip) {
      addressLine += zip + ' ';
    }
    if (city) {
      addressLine += city;
    }
    if (state) {
      addressLine += '(' + state + ')';
    }
    if (country) {
      addressLine += ', ' + country;
    }
    return addressLine;
  }

  /**
   * @ngdoc method
   * @name SharedModule:formattingService#formatSimpleAddress
   * @methodOf SharedModule:formattingService
   *
   * @description Format an address based including address, city and country (multi line)
   *
   * @param {string} address - address
   * @param {string} city - city
   * @param {string} country - country
   *
   * @return {string} The simple address formatted in multiple lines
   */
  public formatSimpleAddress(address:string, city:string, country:string):string {

    let simpleAddress = '';
    if (address) {
      simpleAddress += address + '\n';
    }
    if (city) {
      simpleAddress += city + ', ';
    }
    if (country) {
      simpleAddress += country;
    }
    return simpleAddress;
  }

  /**
   * @ngdoc method
   * @name SharedModule:formattingService#formatValue
   * @methodOf SharedModule:formattingService
   *
   * @description Format a value using a custom format
   *
   * @param {string} format The format type of the value
   * Possible formats: `date`, `lov`, `genericcontact`, `currency`, `localcurrency`, `boolean`, `company`, `subcompany`,
   * `country`, `acslusers`, `contact`, `companyCountry`, `decimal-1`, `decimal-2`, `decimal-3`, ..
   * @param {any} value - value
   *
   * @return {string} The formatted value
   */
  public formatValue(format:string, value:any):string {
    if (format === 'date') {
      //value is in number of millis since epoch format
      value = new Date(value);
      return this.formatDateMedium(value);
    } else if (format === 'lov') {
      return value.value;
    } else if (format === 'genericcontact') {
      return value.description;
    } else if (format === 'currency' || format === 'localcurrency') {
      return this.formatCurrencyL(value);
    } else if (format === 'boolean') {
      return this.formatBoolean(value);
    } else if (format === 'code') {
      return value.code;
    } else if (format === 'company' || format === 'subcompany' || format === 'country') {
      return value.name;
    } else if (format === 'acslusers' || format === 'contact') {
      return this.formatContact(value);
    } else if (format === 'companyCountry') {
      return this.formatCompanyCountryL(value);
    } else if (util.notNullOrUndefined(format) && format.indexOf('decimal-') === 0) {
      return this.formatDecimal(value, format.substring('decimal-'.length));
    } else {
      return value;
    }
  }

  /**
   * @ngdoc method
   * @name SharedModule:formattingService#formatLabel
   * @methodOf SharedModule:formattingService
   *
   * @description Format a label value
   *
   * @param {IValueLabel} value object
   *
   * @return {string} The formatted value
   */
  public formatLabel(value:ILabelledValue):string {
    return value?.label;
  }

  /**
   * @ngdoc method
   * @name SharedModule:formattingService#formatLov
   * @methodOf SharedModule:formattingService
   *
   * @description Format a value from a list of values
   *
   * @param {ILov | ILov[]} value - value
   *
   * @return {string} - value
   */
  public formatLov(value:ILov | ILov[]):string {
    if (!value) {
      return '';
    }

    const values:ILov[] = _.isArray(value) ? value : [value];
    return _.map(values, (item) => item.value || item.description).join(', ');
  }

  /**
   * @ngdoc method
   * @name SharedModule:formattingService#formatContact
   * @methodOf SharedModule:formattingService
   *
   * @description Format a contact in one line including: first name, last name and email address
   *
   * @param {IContact} contact - contact
   *
   * @return {string} Formatted string including the information in one line
   */
  public formatContact(contact:IContact):string {
    let formatted = '';
    if (contact) {
      if (contact.firstname) {
        formatted += contact.firstname;
      }
      if (contact.lastname) {
        formatted += (contact.firstname? ' ' : '') + contact.lastname;
      }
      if (formatted === '') {
        formatted = contact.email;
      }
    }
    return formatted;
  }

  /**
   * @ngdoc method
   * @name SharedModule:formattingService#formatContactFN
   * @methodOf SharedModule:formattingService
   *
   * @description Extract the full name of the contact
   *
   * @param {IContact} contact - contact
   *
   * @return {string} - contact full name
   */
  public formatContactFN(contact:IContact):string {
    return contact? contact.fullName : '';
  }

  /**
   * @ngdoc method
   * @name SharedModule:formattingService#formatFullContact
   * @methodOf SharedModule:formattingService
   *
   * @description Format the contact details including name/first name + last name, email and direct phone
   *
   * @param {IContact} contact - contact
   *
   * @return {string} - contact details in one line
   */
  public formatFullContact = (contact:IContact):string => {
    let fullName;
    if (contact.fullName) {
      fullName = contact.fullName;
    } else if (contact.name) {
      fullName = contact.name;
    } else if (contact.firstname || contact.lastname) {
      fullName = '';
      if (contact.firstname) {
        fullName += contact.firstname;
      }
      if (contact.lastname) {
        fullName += (contact.firstname? ' ' : '') + contact.lastname;
      }
    }
    return this.join(
      fullName,
      contact.email,
      contact.directPhone,
      '\n'
    );
  };

  /**
   * @ngdoc method
   * @name SharedModule:formattingService#formatContactWithPhone
   * @methodOf SharedModule:formattingService
   *
   * @description Format a contact including in first line: (first name, last name or email) and in second line phone number
   *
   * @param {IContact} contact - contact
   *
   * @return {string} Formatted string including the information in two lines
   */
  public formatContactWithPhone = (contact:IContact):string => {
    if (!contact) {
      return '';
    }
    const phone:string = this.formatPhone(contact);
    return `<b>${this.formatContact(contact)}</b>${phone? '<br />' + phone : ''}`;
  };

  /**
   * @ngdoc method
   * @name SharedModule:formattingService#formatContactWithCompany
   * @methodOf SharedModule:formattingService
   *
   * @description Format a contact in one line including: (first name, last name or email address) and company name
   *
   * @param {IContact} contact - contact object
   *
   * @return {string} Formatted string including the information in one line
   */
  public formatContactWithCompany = (contact:IContact):string => {
    let formattedContact:string = this.formatContact(contact);
    if (contact?.company) {
      formattedContact += ` (${contact.company})`;
    }
    return formattedContact;
  };

  /**
   * @ngdoc method
   * @name SharedModule:formattingService#formatContactInfo
   * @methodOf SharedModule:formattingService
   *
   * @description Format a contact including the contact name, contact title and company
   *
   * @param {IContact} contact - contact
   *
   * @return {string} Formatted string including the information in one lines
   */
  public formatContactInfo = (contact:IContact):string => {
    if (!contact) {
      return '';
    }

    return this.join(
      this.formatContact(contact),
      contact?.title || contact?.companyDepartment?.description,
      contact?.company,
      ' - '
    );
  };

  /**
   * @ngdoc method
   * @name SharedModule:formattingService#formatContactInfoDetails
   * @methodOf SharedModule:formattingService
   *
   * @description Format information is being used in the contact-info component for displaying additional information about a contact
   *
   * @param {IContact} contact - contact
   * @param {RoleContactType} contactType - role contact type
   * @param {number} creationDate - role contact creation date
   * @param {string} creationUserName - role contact creator
   *
   * @return {string} Format a contact including the details about the contact
   */
  public formatContactInfoDetails = (contact:IContact, contactType?:RoleContactType|IContactType, creationDate?:number, creationUserName?:string):string => {
    if (!contact) {
      return '';
    }

    const prefix:string = 'https://';
    const linkedinUrl:string = contact?.linkedIn?.startsWith(prefix)? contact.linkedIn.replace(prefix, '') : contact.linkedIn;
    const contactTypeLabel:string = _.isString(contactType) ? contactType.replace(RoleContactType.ORDER_UPDATE, this.translationService.instant('GA.CONTACT_INFO.ORDER_UPDATES_CONTACT')) : contactType?.description;
    return this.join(
      this.translationService.instant('GA.CONTACT_INFO.REGISTERED_ON', { date: this.formatDate(contact.creationDate) }),
      contactTypeLabel && creationDate && creationUserName? this.translationService.instant('GA.CONTACT_INFO.IN_ROLE_SINCE', {
        contactType: contactTypeLabel,
        date: this.formatDate(creationDate),
        user: creationUserName
      }) : undefined,
      contact.lastLoginDate? this.translationService.instant('GA.CONTACT_INFO.LAST_LOGIN_DATE', { label: this.formatDate(contact.lastLoginDate) }) : undefined,
      contact.trainingStatus? this.translationService.instant('GA.CONTACT_INFO.TRAINING_STATUS', { label: this.formatLov(<any>contact.trainingStatus) }) : undefined,
      contact.lastTrainingDate? this.translationService.instant('GA.CONTACT_INFO.LAST_TRAINING_DATE', { label: this.formatDate(<any>contact.lastTrainingDate) }) : undefined,
      contact.relationshipStatus? this.translationService.instant('GA.CONTACT_INFO.RELATIONSHIP_STATUS', { label: this.formatLov(<any>contact.relationshipStatus) }) : undefined,
      contact.preferredCommunication? `${this.translationService.instant('GA.CONTACT_INFO.PREFERRED_COMMUNICATION')}: ${contact.preferredCommunication}` : undefined,
      contact.linkedIn? `<a href="${contact.linkedIn}" target="_blank" title="Linkedin">${linkedinUrl}</a>` : undefined,
      '&nbsp;',
      contact.comments? `${contact.comments}` : undefined,
      '<br />'
    );
  };

  /**
   * @ngdoc method
   * @name SharedModule:formattingService#formatFullContactWithoutEmail
   * @methodOf SharedModule:formattingService
   *
   * @description Format the contact details including name/first name + last name and direct phone
   *
   * @param {IContact} contact - contact
   *
   * @return {string} - contact details in one line
   */
  public formatFullContactWithoutEmail = (contact:IContact):string => {
    if (!contact) {
      return '';
    }

    let fullName;
    if (contact.fullName) {
      fullName = contact.fullName;
    } else if (contact.name) {
      fullName = contact.name;
    } else if (contact.firstname || contact.lastname) {
      fullName = '';
      if (contact.firstname) {
        fullName += contact.firstname;
      }
      if (contact.lastname) {
        fullName += (contact.firstname? ' ' : '') + contact.lastname;
      }
    }
    return this.join(
      fullName,
      contact.directPhone,
      '\n'
    );
  };

  /**
   * @ngdoc method
   * @name SharedModule:formattingService#formatContactSignature
   * @methodOf SharedModule:formattingService
   *
   * @description Format the signature of a contact
   *
   * @param {IContact} contact - contact
   *
   * @return {string} Formatted signature
   */
  public formatContactSignature = (contact:IContactExtended):string => {
    if (!contact) {
      return '';
    }
    return this.join(
      this.formatContact(contact),
      this.join(contact?.title, contact?.companyDepartment?.description, ' - '),
      this.join(
        contact.whatsappId ? `WhatsApp: <a href="https://wa.me/${contact.whatsappId}" target="_blank">${contact.whatsappId}</a>` : '',
        contact.weChatId ? `WeChat: <a href="weixin://dl/chat?${contact.weChatId}" target="_blank">${contact.weChatId}</a>` : '',
        ' | '
      ),
      this.join(
        `Email/Teams: <a href="mailto:${contact.email}" target="_blank">${contact.email}</a>`,
        contact.cellPhone ? `Phone: ${contact.cellPhone}` : '',
        ' | '
      ),
      'Web: <a href="https://www.acslworld.com" target="_blank">acslworld.com</a> | LinkedIn: <a href="https://www.linkedin.com/company/acsl" target="_blank">ACSL</a>',
      `Time zone: ${contact.timezone}`,
      '<br>'
    );
  };

  /**
   * @ngdoc method
   * @name SharedModule:formattingService#formatDecimal
   * @methodOf SharedModule:formattingService
   *
   * @description Format a number including a custom number of decimals
   *
   * @param {number|string} num - number
   * @param {number|string} decimals - number of decimals
   *
   * @return {string} - formatted number
   */
  public formatDecimal(num:number | string, decimals?:number | string):string {
    if (util.isNullOrUndefined(num) || num === '') {
      return '';
    }
    if (util.isNullOrUndefined(decimals)) {
      decimals = 2;
    }
    return Number(num).toFixed(<number>decimals);
  }

  /**
   * @ngdoc method
   * @name SharedModule:formattingService#formatUsername
   * @methodOf SharedModule:formattingService
   *
   * @description format user object to firstName + lastName
   *
   * @param {IContact} user - user object
   *
   * @return {string} - a string that includes: firstName and lastName
   */
  public formatUsername(user:IContact | IUserData):string {
    return user.firstname + ' ' + user.lastname;
  }

  /**
   * @ngdoc method
   * @name SharedModule:formattingService#userToContact
   * @methodOf SharedModule:formattingService
   *
   * @description Extract user information from a contact
   *
   * @param {IUser} user - user object
   *
   * @return {IContact} - contact object including id, firstname, lastname and email
   */
  public userToContact(user:IUser):IContact {
    return {
      id: user.id,
      firstname: user.firstname,
      lastname: user.lastname,
      email: user.email
    };
  }

  /**
   * @ngdoc method
   * @name SharedModule:formattingService#formatLinkName
   * @methodOf SharedModule:formattingService
   *
   * @description Turn document object to readable string
   *
   * @param {ILink} document - document object
   *
   * @return {string} -
   */
  public formatLinkName(document:ILink):string {
    if (util.isNullOrUndefined(document)) {
      return '';
    }

    if (util.notNullOrUndefinedOrEmpty(document.description)) {
      return document.description;
    }

    if (util.notNullOrUndefined(document.fileName)) {
      return document.fileName;
    }

    return '';
  }

  /**
   * @ngdoc method
   * @name SharedModule:formattingService#formatMailAddress
   * @methodOf SharedModule:formattingService
   *
   * @description Strip &lt;email@home.com&gt; from the address
   *
   * @param {string} address -
   *
   * @return {string} -
   */
  public formatMailAddress(address:string):string {
    return address.replace(/\s*<.*>/g, '');
  }

  /**
   * @ngdoc method
   * @name SharedModule:formattingService#formatOEMType
   * @methodOf SharedModule:formattingService
   *
   * @description turns OEM type into human readable string
   *
   * @param {string} type -
   *
   * @return {string} -
   */
  public formatOEMType(type:string):string {
    if (!type) {
      return '';
    }
    if (type.search(/direct/i) >= 0) {
      return 'Direct';
    }
    if (type.search(/distribution/i) >= 0) {
      return 'Distributor';
    }
    if (type.search(/cannot/i) >= 0) {
      return 'Not covered';
    }
    return type;
  }

  /**
   * @ngdoc method
   * @name SharedModule:formattingService#formatCompanyCountry
   * @methodOf SharedModule:formattingService
   *
   * @description
   *
   * @param {ICompanyCountry} companyCountry - companycountry object
   *
   * @return {string} -
   */
  public formatCompanyCountry(companyCountry:ICompanyCountry):string {
    if (!companyCountry || !companyCountry.company || !companyCountry.company.name || !companyCountry.country || !companyCountry.country.name) {
      return '';
    }

    return companyCountry.company.name + ' (' + companyCountry.country.isoCode + ')';
  }

  /**
   * @ngdoc method
   * @name SharedModule:formattingService#formatCompanyCountryL
   * @methodOf SharedModule:formattingService
   *
   * @description Long formatting of CompanyCountry: 'companyName - countryName'
   *
   * @param {ICompanyCountry} companyCountry a CompanyCountry object
   *
   * @return {string} the long formatted CompanyCountry
   */
  public formatCompanyCountryL(companyCountry:ICompanyCountry):string {
    if (!companyCountry || !companyCountry.company || !companyCountry.company.name || !companyCountry.country || !companyCountry.country.name) {
      return '';
    }

    return companyCountry.company.name + ' - ' + companyCountry.country.name;
  }

  /**
   * @ngdoc method
   * @name SharedModule:formattingService#formatCompanyCountryNick
   * @methodOf SharedModule:formattingService
   *
   * @description nickName or the long formatting of CompanyCountry: 'companyName - countryName'
   *
   * @param {ICompanyCountry} companyCountry a CompanyCountry object
   *
   * @return {string} the nickName or the long formatted CompanyCountry
   */
  public formatCompanyCountryNick = (companyCountry:ICompanyCountry):string => {
    return companyCountry?.nickName || this.formatCompanyCountryL(companyCountry);
  };

  /**
   * @ngdoc method
   * @name SharedModule:formattingService#formatCompany
   * @methodOf SharedModule:formattingService
   *
   * @description
   *
   * @param {ICompany} company - company object
   *
   * @return {string} -
   */
  public formatCompany(company:ICompany):string {
    if (company) {
      return company.name;
    }
  }

  /**
   * @ngdoc method
   * @name SharedModule:formattingService#formatBPNicknameCountry
   * @methodOf SharedModule:formattingService
   *
   * @description
   *
   * @param {IBPProfileBasic} bpProfile - bp profile object
   *
   * @return {string} -
   */
  public formatBPNicknameCountry(bpProfile:IBPProfileBasic):string {
    return `${bpProfile?.companyCountryNickname || ''} - ${bpProfile?.country?.name || ''}`;
  }

  /**
   * @ngdoc method
   * @name SharedModule:formattingService#formatDepartment
   * @methodOf SharedModule:formattingService
   *
   * @description
   *
   * @param {IDepartment} Department - department object
   *
   * @return {string} -
   */
  public formatDepartment(department:IDepartment):string {
    if (department) {
      return department.description;
    }
  }

  /**
   * @ngdoc method
   * @name SharedModule:formattingService#formatLocation
   * @methodOf SharedModule:formattingService
   *
   * @description formats location object
   *
   * @param {ILocation} location - location object
   * @param {string=} separator - separator string
   *
   * @return {string} -
   */
  public formatLocation = (location:ILocation, separator?:string):string => {
    if (util.isNullOrUndefined(location)) {
      return;
    }

    separator = separator || '\n';

    return this.join(
      location.name,
      location.address,
      this.join(location.zipCode, location.city, ' '),
      location.state && location.state.name? location.state.name : location.state, //state can be an object, or a String
      this.formatName(location.country),
      separator
    );
  };

  /**
   * @ngdoc method
   * @name SharedModule:formattingService#formatLocationHtml
   * @methodOf SharedModule:formattingService
   *
   * @description formats location in html format
   *
   * @param {ILocation} location - location object
   * @param {string=} separator - separator string
   *
   * @return {string} -
   */
  public formatLocationHtml = (location:ILocationExtended, vat?:{ name:string; number:string }):string => {
    if (location) {
      const companyName =
        location.companyRegisteredName ||
        location.subCompany && location.subCompany.name ||
        location.companyCountry && location.companyCountry.company && location.companyCountry.company.name;

      return this.join(
        companyName? `<b>${companyName}</b>` : '',
        location.office,
        location.address,
        this.join(location.zipCode, location.city, ' '),
        location.state && location.state.name,
        location.companyCountry && location.companyCountry.country && location.companyCountry.country.name && location.companyCountry.country.name.toUpperCase(),
        vat && vat.number? this.join(vat.name || this.translationService.instant('GA.GENERAL.VATTAX'), `<span>${vat.number}</span>`, ' ') : '',
        '<br>'
      );
    }
  };

  /**
   * @ngdoc method
   * @name SharedModule:formattingService#formatContactHtml
   * @methodOf SharedModule:formattingService
   *
   * @description formats contact in html format
   *
   * @param {IContact} contact - contact object
   * @param {string=} separator - separator string
   *
   * @return {string} -
   */
  public formatContactHtml = (contact:IContact):string => {
    if (contact) {
      return this.join(
        `<b>${contact.fullName}</b>`,
        contact.email,
        contact.directPhone,
        '<br>'
      );
    }
  };

  /**
   * @ngdoc method
   * @name SharedModule:formattingService#formatCountry
   * @methodOf SharedModule:formattingService
   *
   * @description Formats country object
   *
   * @param {ICountry} value - location object
   *
   * @return {string} - country name
   */
  public formatCountry = (value:ICountry):string => {
    return this.formatName(value);
  };

  /**
   * @ngdoc method
   * @name SharedModule:formattingService#formatState
   * @methodOf SharedModule:formattingService
   *
   * @description Formats state object
   *
   * @param {IState} value - state object
   *
   * @return {string} - state name
   */
  public formatState = (value:IState):string => {
    return this.formatName(value);
  };

  /**
   * @ngdoc method
   * @name SharedModule:formattingService#formatTicketCategoryType
   * @methodOf SharedModule:formattingService
   *
   * @description Extract the ticket category type
   *
   * @param {ITicketCategory} ticket category
   *
   * @return {string} - category type
   */
  public formatTicketCategory(category:ITicketCategory):string {
    if (category) {
      return category.description;
    }
  }


  /**
   * @ngdoc method
   * @name SharedModule:formattingService#formatContactType
   * @methodOf SharedModule:formattingService
   *
   * @param {IContactType} ct - contact type
   *
   * @return {string} - contact type description
   */
  public formatContactType(ct:IContactType):string {
    if (ct) {
      return ct.description;
    }
  }

  /**
   * @ngdoc method
   * @name SharedModule:formattingService#formatPhone
   * @methodOf SharedModule:formattingService
   *
   * @param {IContact} contact - contact object
   *
   * @return {string} - phone number
   */
  public formatPhone(contact:IContact):string {
    let phone:string = '';
    if (contact) {
      phone = contact.directPhone;
      if (!phone) {
        phone = contact.officePhone;
      }
      if (!phone) {
        phone = contact.cellPhone;
      }
    }

    return phone;
  }

  /**
   * @ngdoc method
   * @name SharedModule:formattingService#formatEmail
   * @methodOf SharedModule:formattingService
   *
   * @param {IContact} contact - contact object
   *
   * @return {string} - link from email
   */
  public formatEmail(contact:IContact):string {
    const email = contact.email;

    return '<a href="mailto:' + email + '">' + email + '</a>';
  }

  /**
   * @ngdoc method
   * @name SharedModule:formattingService#formatDays
   * @methodOf SharedModule:formattingService
   *
   * @param {Object} days The number of days
   *
   * @return {string} The formatted number of days
   */
  public formatDays(days:string):string {
    if (util.isNullOrUndefined(days)) {
      return '';
    }

    if (!$.isNumeric(days)) {
      return days;
    }

    if (parseInt(days) === 1) { //also matches '1'
      return days + ' day';
    }

    return days + ' days';
  }

  /**
   * @ngdoc method
   * @name SharedModule:formattingService#formatBusinessDays
   * @methodOf SharedModule:formattingService
   *
   * @param {Object} days The number of days
   *
   * @return {string} The formatted number of days
   */
  public formatBusinessDays(days:string | number):string {
    if (util.isNullOrUndefined(days)) {
      return '';
    }

    if (!$.isNumeric(days)) {
      return <string>days;
    }

    if (parseInt(<string>days) === 1) { //also matches '1'
      return this.translationService.instant('GA.GENERAL.BUSINESS_DAY_1');
    }

    return this.translationService.instant('GA.GENERAL.BUSINESS_DAY_MULTIPLE', { days });
  }

  public formatPaymentTerms = (days:string|number):string => {
    if (util.isNullOrUndefined(days)) {
      return '';
    }

    if (!$.isNumeric(days)) {
      return <string>days;
    }

    if (parseInt(<string>days) === 0) {
      return 'Prepayment';
    }

    return this.formatDays(<string>days);
  };

  //http://stackoverflow.com/questions/2901102/how-to-print-a-number-with-commas-as-thousands-separators-in-javascript
  /**
   * @ngdoc method
   * @name SharedModule:formattingService#formatNumber
   * @methodOf SharedModule:formattingService
   *
   * @description Format a number using a custom format
   *
   * @param {number} number - number
   * @param {format=} format - format
   *
   * @returns {any} - The number in the custom format
   */
  public formatNumber(number:number | string, format?:string):string {
    if (util.isNullOrUndefinedOrEmpty(number)) {
      return '';
    }

    format = format || '#,##0.##';

    //format.js cannot cope with #,##0.00;-#,##0.00
    format = format.replace(/([^;]*);.*/, '$1');

    //workaround for bug in format.js
    if (format.indexOf('.') === -1) {
      format += '.';
    }

    return this.window.format(format, number);
  }

  /**
   * @ngdoc method
   * @name SharedModule:formattingService#formatAmount
   * @methodOf SharedModule:formattingService
   *
   * @description Format an amount using a currency and precision
   *
   * @param {number|string} price - number
   * @param {string} currency - currency
   *
   * @returns {any} - The amount in the custom format
   */
  public formatAmount = (price:number | string, currency:string | ICurrency):string => {
    if (util.isNaNOrNullOrUndefined(price) || price === '') {
      return '';
    }

    const precision:number = this.currencyService.getDecimals(<string>currency);

    let num;
    if (_.isNumber(price)) {
      let format = '#,##0.';
      for (let i = 0; i < precision; i++) {
        format += '0';
      }
      num = this.formatNumber(price, format);
    } else {
      num = price;
    }

    //noinspection JSAccessibilityCheck
    currency = _.isString(currency)? currency : this.formatCurrencyS(currency);

    return num + ' ' + currency;
  };

  /**
   * @ngdoc method
   * @name SharedModule:formattingService#formatName
   * @methodOf SharedModule:formattingService
   *
   * @description Retrieve the string name from an object
   *
   * @param {any} value - value
   *
   * @returns {string} -
   */
  public formatName(value:any):string {
    if (!value) {
      return '';
    }

    return value.name;
  }

  /**
   * @ngdoc method
   * @name SharedModule:formattingService#formatDescription
   * @methodOf SharedModule:formattingService
   *
   * @description Retrieve the string description from an object
   *
   * @param {any} value - value
   *
   * @returns {string} -
   */
  public formatDescription(value:any):string {
    if (!value) {
      return '';
    }

    return value.description;
  }

  /**
   * @ngdoc method
   * @name SharedModule:formattingService#getAuditInfo
   * @methodOf SharedModule:formattingService
   *
   * @param {IContact} contact - value
   * @param {number} date - The value of a date
   * @param {string} prefix - A text that will appear at the begining of the returned string (eg: 'Created' or 'Last modification')
   *
   * @returns {string} - A text like: Last modification by Marius Hirjanu (ACSL) on 06/02/2019 13:13:20 UTC
   */
  public formatAudit(contact:IContact, date:number | string | Date, prefix?:string):string {
    let auditInfo:string;
    if (contact || date) {
      if (prefix) {
        auditInfo = prefix;
      }
      if (contact) {
        auditInfo += ` by <strong>${contact.fullName} (${contact.company})</strong>`;
      }
      if (date) {
        auditInfo += ` on <strong>${this.formatDate(date, DEFAULT_DATE_FORMAT)}</strong>`;
      }
    }
    return auditInfo;
  }


  /**
   * @ngdoc method
   * @name SharedModule:formattingService#formatPct
   * @methodOf SharedModule:formattingService
   *
   * @description Formats a number as a percentage
   *
   * @param {number} pct -
   * @param {boolean=} multiply Should the number be multiplied by 100?
   * @param {string=} format allows to format the percentage in the provided format
   *
   * @returns {string} percentage
   * ()
   */
  public formatPct = (pct:number, multiply?:boolean, format?:string):string => {
    if (util.isNullOrUndefined(pct) || isNaN(Number(pct))) {
      return '';
    }

    if (multiply) {
      pct *= 100;
    }

    format = format || '#,##0.## %';

    return this.formatNumber(pct, format);
  };

  public formatPct2 = (value:number) => this.formatPct(value, undefined, '(0.## %)');

  /**
   * @ngdoc method
   * @name SharedModule:formattingService#removeHtmlBreaks
   * @methodOf SharedModule:formattingService
   *
   * @description Remove the html line breaks from a piece of text containing html tags
   *
   * @param {string} innerHtml -
   * @param {string} replaceValue - replace breaks with the given value (default: whitespace character)
   *
   * @returns {string} formatted text removing the html line breaks
   */
  public removeHtmlBreaks(innerHtml:string, replaceValue?:string):string {
    if (util.isNullOrUndefined(innerHtml)) {
      return '';
    }
    return innerHtml.replace(/<br\s*\/?>/gi, replaceValue || ' ');
  }

  /**
   * @ngdoc method
   * @name SharedModule:formattingService#removeHtmlTags
   * @methodOf SharedModule:formattingService
   *
   * @description Remove the html tags from a piece of text
   *
   * @param {string} innerHtml -
   *
   * @returns {string} formatted text removing the html tags
   */
  public removeHtmlTags(innerHtml:string):string {
    if (util.isNullOrUndefined(innerHtml)) {
      return '';
    }
    return innerHtml.replace(/<(?:.|\\n)*?>/gm, '');
  }

  /**
   * @ngdoc method
   * @name SharedModule:formattingService#formatIdentifier
   * @methodOf SharedModule:formattingService
   *
   * @description Create an identifier from a given text
   *
   * @param {string} text -
   *
   * @returns {string} formatted text after the following operations: removing all non alfanumeric characters, replacing white-space character with hyphen symbol and converting to lower case text
   */
  public formatIdentifier(text:string):string {
    let formattedText = text.replace(/[^\w\s]/gi, '');
    formattedText = formattedText.replace(/\s+/g, '-');
    return formattedText.toLowerCase();
  }

  /**
   * @ngdoc method
   * @name SharedModule:formattingService#getRankingLabelClass
   * @methodOf SharedModule:formattingService
   *
   * @description formats ranking
   *
   * @param {Object} ranking - ranking object
   *
   * @return {string} ranking-label class
   */
  public getRankingLabelClass(ranking: IObject): string {
    const rankingValue: string = ranking?.value?.toLowerCase();
    if (rankingValue === 'preferred') {
      return 'bg-success';
    } else if (rankingValue === 'never use this bp') {
      return 'bg-danger';
    } else {
      return 'bg-primary';
    }
  }

  /**
   * @ngdoc method
   * @name SharedModule:formattingService#escapeQuotes
   * @methodOf SharedModule:formattingService
   *
   * @description Escape quotes from a given string
   *
   * @param {string} text -
   *
   * @returns {string} text containing escaped quotes
   */
  public escapeQuotes(text:string):string {
    return text.replace(/([\"\'])/g, '\\$1');
  }


  /**
   * @ngdoc method
   * @name SharedModule:formattingService#snakeCase
   * @methodOf SharedModule:formattingService
   *
   * @description Transforms a string from camelCase to snakeCase e.g. thisIsCamelCase to this-is-camel-case
   *
   * @param {string} text -
   * @param {string} separator The separator to use (defaults to '-')
   *
   * @returns {string} The snake case string
   */
  public snakeCase(text:string, separator:string = '-') {
    if (!text) {
      return undefined;
    }

    separator = separator || '-';
    return text.replace(/[A-Z]/g, (letter, pos) => {
      return (pos? separator : '') + letter.toLowerCase();
    });
  }

  public camelCase(text:string) {
    if (!text) {
      return text;
    }

    return text.replace(/-([\da-z])/g, (all, letter) => {
      return (letter + '').toUpperCase();
    });
  }

  public formatLanguage(lang:ILanguage):string {
    if (!lang) {
      return '';
    }
    return lang.name + (lang.nameInLanguage? ` (${lang.nameInLanguage})` : '');
  }

  /**
   * @ngdoc method
   * @name SharedModule:formattingService#formatRecipients
   * @methodOf SharedModule:formattingService
   *
   * @description Format the list of recipients to comma separated values
   *
   * @param {(IContact|number)[]} recipients - A list of recipients, eg. [ { "id": 73897, "firstname": "Marius", "lastname": "Hirjanu" }, "manual_email@acslworld.com" ]
   *
   * @returns {string} The comma separated values, eg. 73897, manual_email@acslworld.com
   */
  public formatRecipients = (recipients:(IContact | string)[]):string => {
    if (util.isNullOrUndefined(recipients)) {
      return undefined;
    }

    const listRecipients:(number | string)[] = [];
    _.each(recipients, (recipient:IContact | string) => {
      listRecipients.push(_.isObject(recipient)? (<IContact>recipient).id : <string>recipient);
    });
    return this.join(listRecipients, ',');
  };

  /**
   * @ngdoc method
   * @name SharedModule:formattingService#formatCSV
   * @methodOf SharedModule:formattingService
   * @description Format the list of objects to comma separated values
   *
   * @param {(IObject)[]} listObjects - A list of object containing 'id' key, e.g. [ { "id": 73897, "firstname": "Marius", "lastname": "Hirjanu" }, { "id": 522, "firstname": "Koen", "lastname": "Weyn" }, ... ]
   *
   * @returns {string} The comma separated values, e.g. 73897,522
   */
  public formatCSV = (listObjects:IObject[]):string => {
    if (util.isNullOrUndefined(listObjects)) {
      return undefined;
    }

    const csv:string[] = [];
    _.each(listObjects, (object:IObject) => {
      if(object?.id){
        csv.push(object.id);
      }
    });
    return this.join(csv, ',');
  };

  /**
   * @ngdoc method
   * @name SharedModule:formattingService#substituteText
   * @methodOf SharedModule:formattingService
   * @description Replaces the variables in a text with the ones from vars
   *
   * @param {string} text - A text containing variable placeholders, e.g. This bowl with ID ${bowl.id} contains ${apples} and ${pears}.
   * @param {} vars - An object containing the replacement variables, e.g. { bowl: { id: 234 } }, apples: 'my apples', pears: 'my pears'}
   *
   * @returns {string} A text with the substitute variables, e.g. This bowl with ID 234 contains my apples and my pears.
   */
  public substituteText(text:string, vars:{}):string {
    return text.replace(/\$\{([^}]*)\}/g, (match:string, key:string) => {
      let value:string = util.extractValue(vars[ key ]);
      if(util.isNullOrUndefined(value)){
        value = util.getValue(vars, key);
      }
      return util.notNullOrUndefinedOrEmpty(value)? value : match;
    });
  }

  /**
   * @ngdoc method
   * @name SharedModule:formattingService#replaceDiacritics
   * @methodOf SharedModule:formattingService
   *
   * @description Replaces the diacritics of a text
   *
   * @param {string} text - A text containing diacritics
   *
   * @returns {string} A text in which the diacritics were replaced with their equivalence, eg. München -> Munchen, Montréal -> Montreal, Genève -> Geneve
   */
  public replaceDiacritics(text:string):string {
    try {
      return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    } catch (e) {
      // fallback support for browsers non-compatible with normalize()
      _.each(DIACRITICS_MAP, (letter) => {
        text = text.replace(letter.base, letter.replacement);
      });
      return text;
    }
  }
}